import {compose, withProps} from 'react-recompose';

import {connect} from 'react-redux';
import config from '../../config';
import {getIddZoneCountries} from '../../lib/country-utils.js';
import formatAmount from '../../lib/formatAmount';
import I18n from '../../lib/i18n';
import {customPaymentMeanSelector} from '../../modules/Customer/selectors';
import {iddZoneSelector} from '../../modules/International/IDD';
import {isLoadingSelector} from '../../modules/LoadingStatus';
import {
  availableTopUpsSelectorForType,
  selectedTopUpsPriceSelector,
} from '../../modules/TopUps';
import {fetchAvailableTopUps} from '../../modules/TopUps/actions';
import {
  setCurrentVoucherDetails,
  voucherCurrentVoucherDetailsSelector,
} from '../../modules/Voucher';
import theme from '../../theme-config';
import type {TopUpFlaggedProps as FlaggedProps} from './TopUp';
import TopUp from './TopUp.container';
import {getVisibleNumbers} from './lib';

const mapStateToProps = state => ({
  availableTopUps: availableTopUpsSelectorForType(
    state,
    config.international.IDD,
  ),
  countryList: getIddZoneCountries(config.iddCountries, iddZoneSelector(state)),
  iddZone: iddZoneSelector(state),
  price: selectedTopUpsPriceSelector(state),
  paymentMean: customPaymentMeanSelector(state),
  isConfirmationLoading: isLoadingSelector(state, 'topUpConfirmation'),
  currentVoucherDetails: voucherCurrentVoucherDetailsSelector(state),
});

const mapDispatchToProps = dispatch => ({
  fetchAvailableTopUps: () =>
    dispatch(fetchAvailableTopUps(config.international.IDD)),
  setCurrentVoucherDetails: (object: Object) =>
    dispatch(setCurrentVoucherDetails(object)),
});

const modalTopUpExplanation = (
  price: number,
  paymentMean?: Object,
  walletBalance?: number,
  currentVoucherDetails?: Object,
) => {
  if (price) {
    const formattedPrice = formatAmount(price);

    if (walletBalance && walletBalance - price >= 0) {
      const textContent = I18n.t(
        currentVoucherDetails
          ? 'ChooseInternationalOptions.modalTopUpExplanation.walletVoucher'
          : 'ChooseInternationalOptions.modalTopUpExplanation.wallet',
        {
          price: formattedPrice,
        },
      );
      return textContent;
    }

    if (paymentMean) {
      const textContent = I18n.t(
        currentVoucherDetails
          ? 'ChooseInternationalOptions.modalTopUpExplanation.creditCardVoucher'
          : 'ChooseInternationalOptions.modalTopUpExplanation.creditCard',
        {
          cardNumber: getVisibleNumbers(paymentMean?.functionalId),
          price: formattedPrice,
        },
      );
      return textContent;
    }
  }
};

const footerTopUpExplanation = (price: number, walletBalance?: number) => {
  let paymentMean = I18n.t('paymentMean.CREDIT_CARD.name').toLowerCase();

  if (walletBalance && walletBalance - price >= 0) {
    paymentMean = I18n.t('paymentMean.WALLET.name');
    return I18n.t('ChooseInternationalOptions.footerTopUpExplanation', {
      paymentMean,
    });
  }

  return I18n.t('ChooseInternationalOptions.footerTopUpExplanation', {
    paymentMean,
  });
};

const withInternationalTopUpProps = ({
  iddZone,
  paymentMean,
  price,
  currentVoucherDetails,
}): FlaggedProps => ({
  confirmButtonText: I18n.t('Dashboard.TopUp.topUpNow'),
  headerTextLine1: I18n.t('ChooseInternationalOptions.textLine1'),
  headerTitle: I18n.t('ChooseInternationalOptions.zone', {iddZone}),
  iconColor: theme.topUp.header.iconColor,
  headerImage: theme.internationalImage,
  iconName: 'international',
  orderSummaryTitle: I18n.t('ChooseInternationalOptions.title'),
  modalTopUpExplanation: (walletBalance?: number) =>
    modalTopUpExplanation(
      price,
      paymentMean,
      walletBalance,
      currentVoucherDetails,
    ),
  footerTopUpExplanation: (walletBalance?: number) =>
    footerTopUpExplanation(price, walletBalance),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(withInternationalTopUpProps),
)(TopUp);
